import * as implementationActions from '@Actions/implementation';
import { error as notificationError, success } from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	getConfigV2ZipService,
	deployGreengrassV2Service,
	getMsiInstallerUrlService as getInstaller,
	createMsiInstallerService as createMsi,
	statusMsiInstallerService as installerStatus,
	getConfigCodeService,
	getDataConnectorStatusByDevice,
	setDataConnectorStatusByDevice,
	deployDevice,
	createConfigCodeDynamicService,
	deployDeviceV2,
} from '@Services/implementation.service';
import {
	CertificateStatusResponse,
	CertificateStatusUpdateRequestWithThingName,
} from '@Types/certificateStatus.types';
import {
	DeviceConfCodeRequestBody,
	DeviceConfCodeResponse,
} from '@Types/deviceConfig.types';
import { BuildStatus } from '@Types/implementation';
import { AxiosResponse } from 'axios';
import { Task } from 'redux-saga';
import { cancel, delay, put, retry, takeLatest } from 'redux-saga/effects';
import { t } from 'i18next';
import { TimeRange } from '@Utils/timeRange';
import { DeployGreengrassV2Payload } from '@Actions/implementation';

function* getConfigV2ZipSaga() {
	const { getConfigV2ZipResolved, getConfigV2ZipRejected } =
		implementationActions;
	try {
		const response: AxiosResponse<BlobPart> = yield retry(
			2,
			1500,
			getConfigV2ZipService,
		);
		if (response.data) {
			yield put(getConfigV2ZipResolved(response.data));
		} else {
			yield put(getConfigV2ZipRejected());
			yield put(notificationError('Error getting zip'));
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error getting zip'));
		yield put(getConfigV2ZipRejected());
	}
}

function* watchGetConfigV2Zip() {
	yield takeLatest(implementationActions.getConfigV2Zip, getConfigV2ZipSaga);
}

function* deployGreengrassV2Saga(
	action: PayloadAction<DeployGreengrassV2Payload>,
) {
	const { deployDevice, deployGreengrassV2Rejected } = implementationActions;
	try {
		const { newApi, deploymentOption } = action.payload;
		const apiService = newApi ? deployDeviceV2 : deployGreengrassV2Service;
		type RT = AxiosResponse<{ location: string }>;
		const response: RT = yield retry(2, 1500, apiService, deploymentOption);
		if (response.data) {
			yield put(deployDevice());
		} else {
			yield put(notificationError('Error deploying group'));
			yield put(deployGreengrassV2Rejected());
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error deploying group'));
		yield put(deployGreengrassV2Rejected());
	}
}

function* watchDeployGreengrassV2() {
	yield takeLatest(
		implementationActions.deployGreengrassV2,
		deployGreengrassV2Saga,
	);
}

function* downloadMsiInstallerSaga(action: PayloadAction<{ global: boolean }>) {
	const { payload } = action;
	const {
		getDownloadMsiInstallerUrlResolved,
		getDownloadMsiInstallerUrlRejected,
	} = implementationActions;
	try {
		const response: AxiosResponse<{
			downloadUrl: string;
		}> = yield retry(1, 1500, getInstaller, { global: payload.global });
		if (response.data)
			yield put(getDownloadMsiInstallerUrlResolved(response.data));
		else yield put(getDownloadMsiInstallerUrlRejected());
	} catch (e) {
		yield put(getDownloadMsiInstallerUrlRejected());
	}
}

function* watchDownloadMsiInstaller() {
	yield takeLatest(
		implementationActions.getDownloadMsiInstallerUrl,
		downloadMsiInstallerSaga,
	);
}

let statusTask: Task;
function* generateMsiFileInstallerSaga(
	action: PayloadAction<{ global: boolean }>,
) {
	const { payload } = action;
	const { generateMsiFileResolved, generateMsiFileRejected } =
		implementationActions;
	try {
		cancel(statusTask);
		type StatusResp = AxiosResponse<unknown>;
		yield put(implementationActions.blockMsiFileRegenerate());
		const response: StatusResp = yield retry(1, 1500, createMsi);
		if (response.data) {
			yield delay(10000);
			yield put(generateMsiFileResolved());
			yield put(
				implementationActions.checkStatusMsiFile({
					global: payload.global,
				}),
			);
		} else {
			yield put(generateMsiFileRejected());
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(notificationError('Error generating msi installer file'));
		yield put(generateMsiFileRejected());
	}
}

function* watchGenerateMsiFileInstaller() {
	yield takeLatest(
		implementationActions.generateMsiFile,
		generateMsiFileInstallerSaga,
	);
}

function* checkStatusMsiFileInstallerSaga(
	action: PayloadAction<{ global: boolean }>,
) {
	const { payload } = action;
	const {
		checkStatusMsiFileResolved: statusResolved,
		checkStatusMsiFileRejected,
		getDownloadMsiInstallerUrlResolved: urlResolved,
	} = implementationActions;
	try {
		type StatusRes = AxiosResponse<BuildStatus>;
		const response: StatusRes = yield retry(1, 1500, installerStatus);
		if (response.data) {
			yield put(statusResolved({ buildStatus: response.data }));
		} else {
			yield put(checkStatusMsiFileRejected());
			throw new Error('OperationStatus != SUCCESS');
		}

		// ? Url
		try {
			type UrlRes = AxiosResponse<{ downloadUrl: string }>;
			const urlResponse: UrlRes = yield retry(1, 1500, getInstaller, {
				global: payload.global,
			});
			if (urlResponse.data.downloadUrl)
				yield put(urlResolved(urlResponse.data));
		} catch (error) {
			console.error(error);
		}

		if (!response.data.buildComplete) {
			yield delay(1000 * 60); // ? 1 min
			yield put(
				implementationActions.checkStatusMsiFile({
					global: payload.global,
				}),
			);
		}
	} catch (e) {
		yield put(notificationError('Error generating msi installer file'));
		yield put(checkStatusMsiFileRejected());
	}
}

function* genConfigCodeSaga(action: PayloadAction<DeviceConfCodeRequestBody>) {
	const { payload } = action;
	const {
		genConfCodeResolved,
		genConfCodeExpire,
		genConfCodeRejected,
		genConfCode,
	} = implementationActions;
	try {
		type CodeRes = AxiosResponse<DeviceConfCodeResponse>;
		const useDefaultPorts = payload?.useDefaultPorts || false;
		const codeRes: CodeRes = yield retry(1, 1500, getConfigCodeService, {
			poll: payload.poll ?? true,
			useDefaultPorts,
		});
		const expiresAt = codeRes?.data?.createdAt || 0;
		const minutesLeft = TimeRange.minutesDifference(expiresAt);
		const isExpired = codeRes.data.state === 'stale' || minutesLeft < 0;
		const isCreating = codeRes.data.state === 'in_progress';
		const isFinished = codeRes.data.state === 'finished';
		if (isCreating) {
			yield delay(5 * 1000);
			yield put(
				genConfCode({
					poll: true,
					deviceId: payload.deviceId,
					firstTime: payload.firstTime,
				}),
			); // ? trigger again (poll)
		} else if (isExpired) {
			yield put(genConfCodeRejected());
		} else if (isFinished) {
			yield put(genConfCodeResolved(codeRes.data));
			yield put(genConfCodeExpire()); // ? 1hr timer
		}
	} catch (e) {
		yield put(notificationError('Error getting code'));
		yield put(genConfCodeRejected());
	}
}

function* genConfigCodeSagaDynamic(
	action: PayloadAction<DeviceConfCodeRequestBody>,
) {
	const { payload } = action;
	const {
		genConfCodeDynamicResolved,
		genConfCodeExpire,
		genConfCodeDynamicRejected,
		genConfCode,
	} = implementationActions;
	try {
		type CodeRes = AxiosResponse<DeviceConfCodeResponse>;
		const useDefaultPorts = payload?.useDefaultPorts || false;
		const codeRes: CodeRes = yield retry(
			1,
			1500,
			createConfigCodeDynamicService,
			{ useDefaultPorts },
		);
		const expiresAt = codeRes?.data?.createdAt || 0;
		const minutesLeft = TimeRange.minutesDifference(expiresAt);
		const isExpired = codeRes.data.state === 'stale' || minutesLeft < 0;
		const isCreating = codeRes.data.state === 'in_progress';
		const isFinished = codeRes.data.state === 'finished';
		if (isCreating) {
			yield delay(5 * 1000);
			yield put(
				genConfCode({
					poll: true,
					deviceId: payload.deviceId,
					firstTime: payload.firstTime,
				}),
			); // ? trigger again (poll)
		} else if (isExpired) {
			yield put(genConfCodeDynamicRejected());
		} else if (isFinished) {
			yield put(genConfCodeDynamicResolved(codeRes.data));
			yield put(genConfCodeExpire()); // ? 1hr timer
		}
	} catch (e) {
		yield put(notificationError('Error getting code'));
		yield put(genConfCodeDynamicRejected());
	}
}

function* expireConfigCodeSaga() {
	const { genConfCodeResolved } = implementationActions;
	yield delay(60 * 60 * 1000); // ? 1 hr
	yield put(genConfCodeResolved({ code: '', createdAt: 0, state: 'idle' }));
}

function* watchCheckStatusMsiFileInstaller() {
	statusTask = yield takeLatest(
		implementationActions.checkStatusMsiFile,
		checkStatusMsiFileInstallerSaga,
	);
}

function* watchGenConfigCode() {
	statusTask = yield takeLatest(
		implementationActions.genConfCode,
		genConfigCodeSaga,
	);
}

function* watchGenConfigCodeDynamic() {
	statusTask = yield takeLatest(
		implementationActions.genConfCodeDynamic,
		genConfigCodeSagaDynamic,
	);
}

function* watchExpireCode() {
	statusTask = yield takeLatest(
		implementationActions.genConfCodeExpire,
		expireConfigCodeSaga,
	);
}

function* getCertStatusSaga(action: PayloadAction<string>) {
	const { getCertStatusResolved, getCertStatusRejected } =
		implementationActions;
	try {
		type CertStatusRes = AxiosResponse<CertificateStatusResponse>;
		const certStatusRes: CertStatusRes = yield retry(
			1,
			1500,
			getDataConnectorStatusByDevice,
			action.payload,
		);
		if (certStatusRes.data.status) {
			yield put(getCertStatusResolved(certStatusRes.data));
		}
	} catch (e) {
		yield put(notificationError(t('Error Checking Data Connector')));
		yield put(getCertStatusRejected());
	}
}

function* watchGetCertStatus() {
	statusTask = yield takeLatest(
		implementationActions.getCertStatus,
		getCertStatusSaga,
	);
}

function* setCertStatusSaga(
	action: PayloadAction<CertificateStatusUpdateRequestWithThingName>,
) {
	const { setCertStatusResolved, setCertStatusRejected } =
		implementationActions;
	try {
		type CertStatusRes = AxiosResponse<CertificateStatusResponse>;
		const certStatusRes: CertStatusRes = yield retry(
			1,
			1500,
			setDataConnectorStatusByDevice,
			action.payload.thingName,
			action.payload.desiredStatus,
		);
		if (certStatusRes.data.status) {
			yield put(setCertStatusResolved(certStatusRes.data));
		}
	} catch (e) {
		yield put(notificationError(t('Error Updating Data Connector')));
		yield put(setCertStatusRejected());
	}
}

function* watchSetCertStatus() {
	statusTask = yield takeLatest(
		implementationActions.setCertStatus,
		setCertStatusSaga,
	);
}

function* deployDeviceSaga() {
	const { deployGreengrassV2Resolved, deployGreengrassV2Rejected } =
		implementationActions;
	try {
		yield retry(1, 1500, deployDevice);
		yield put(deployGreengrassV2Resolved());
		yield put(success('Implementation Successfully Deployed!'));
	} catch (error) {
		console.error(error);
		yield put(notificationError('Error deploying group'));
		yield put(deployGreengrassV2Rejected());
		throw new Error('OperationStatus != SUCCESS');
	}
}

function* watchDeviceDeploy() {
	statusTask = yield takeLatest(
		implementationActions.deployDevice,
		deployDeviceSaga,
	);
}

export default [
	watchGetConfigV2Zip(),
	watchDeployGreengrassV2(),
	watchDownloadMsiInstaller(),
	watchGenerateMsiFileInstaller(),
	watchCheckStatusMsiFileInstaller(),
	watchGenConfigCode(),
	watchGenConfigCodeDynamic(),
	watchExpireCode(),
	watchGetCertStatus(),
	watchSetCertStatus(),
	watchDeviceDeploy(),
];
